import React, { useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@Hooks/ReduxHooks'
import routes from '@Constants/Routes'
import { BounceLoader } from 'react-spinners'
import styled from 'styled-components'

type Props = {
	redirectURL?: string
	/**
	 * Overrides the default `!isAuth` check when provided (true = render).
	 * The /signup step routes pass their onboarding conditions here — an
	 * authenticated student with an unverified phone must still reach the OTP
	 * form at /signup/1 while RoutingProvider is forcing them onto it, so the
	 * default authed-bounce would ping-pong home ↔ /signup/1 forever.
	 */
	validator?: boolean
}

/**
 * Wraps a route that only makes sense signed out.
 *
 * Deep-linking to /login while already signed in used to render the login form,
 * which is a dead end: submitting it is pointless and the only way out is to
 * navigate away by hand.
 *
 * Gated on `authReady`, not `isAuth` alone. /auth/me is the only thing that can
 * answer whether the session is live, and before that answer arrives `isAuth`
 * is false for everyone — acting on it would show the form to a signed-in
 * student anyway (the bug) and, once the answer landed, yank a genuine guest
 * off the page mid-typing. A loader fills that one round trip: rendering
 * nothing here looked like a broken page on a slow connection.
 *
 * The redirect fires only when the user ARRIVES disallowed (a deep link while
 * signed in). When `allowed` flips false in place — they just signed in on
 * this very page — the flow that flipped it owns the navigation: Login pushes
 * the stored return URL or /verify-device, and a <Navigate> rendered here
 * raced that push and won, wrongly. <Navigate> navigates in a passive effect,
 * nothing else on the login page re-renders between the auth flip and Login's
 * push, so React flushed the pending effect AFTER the push and this
 * component's `replace` overwrote the return URL with home.
 *
 * Redirects with `replace` so Back does not land on the route we just bounced
 * off.
 */
const PublicOnlyRoute: React.FC<Props> = ({ children, redirectURL = routes.home, validator }) => {
	const { isAuth, authReady } = useAppSelector(state => state.auth)
	const { pathname } = useLocation()
	// Keyed by pathname, not by component instance: the /signup steps mount this
	// guard at the same route-tree position, so react-router reuses one fiber
	// across /signup ↔ /signup/1 and a per-instance ref would carry the FIRST
	// step's arrival decision into the next. Re-deciding per pathname makes
	// "arrival" mean what it says.
	const entryDecision = useRef<'allowed' | 'blocked' | null>(null)
	const entryPathname = useRef<string | null>(null)

	if (!authReady) {
		return (
			<LoaderWrapper>
				<BounceLoader loading color="#6c63ff" />
			</LoaderWrapper>
		)
	}

	const allowed = validator ?? !isAuth
	if (entryPathname.current !== pathname) {
		entryPathname.current = pathname
		entryDecision.current = allowed ? 'allowed' : 'blocked'
	}

	if (!allowed && entryDecision.current === 'blocked') return <Navigate to={redirectURL} replace />
	return <>{children}</>
}

const LoaderWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 40vh;
`

export default PublicOnlyRoute
